<template>
  <div class="amzprod-page">
    <!-- Header -->
    <div class="ap-header">
      <div>
        <span class="header-eyebrow">Amazon</span>
        <h2 class="header-title">Productos Amazon</h2>
      </div>
      <div class="header-actions">
        <button class="btn-ap btn-ap--primary" @click="cargarProductos" :disabled="loading">
          <i :class="loading ? 'fa-solid fa-spinner fa-spin' : 'fa-solid fa-rotate'"></i>
          {{ loading ? 'Cargando...' : 'Recargar' }}
        </button>
      </div>
    </div>

    <!-- Filters -->
    <div class="filters-bar">
      <div class="filter-group">
        <label class="filter-label">Marketplace</label>
        <div class="mkt-pills">
          <button v-for="opt in marketplaceOptions" :key="opt.value" :class="['mkt-pill', { active: marketplace === opt.value }]" @click="marketplace = opt.value; cargarProductos()">{{ opt.label }}</button>
        </div>
      </div>
      <div class="search-box">
        <i class="fa-solid fa-magnifying-glass search-box__icon"></i>
        <input type="text" class="search-box__input" placeholder="Filtrar titulo, SKU, ASIN..." v-model="filtro" />
      </div>
      <div class="filter-group">
        <label class="filter-label">Estado</label>
        <div class="estado-pills">
          <button :class="['pill', { active: !filtroEstado }]" @click="filtroEstado = null">Todos</button>
          <button v-for="est in estadoOptions" :key="est" :class="['pill', { active: filtroEstado === est }]" @click="filtroEstado = est">{{ est }}</button>
        </div>
      </div>
      <span class="filter-count" v-if="productos.length > 0">{{ productosFiltrados.length }} / {{ productos.length }}</span>
    </div>

    <!-- Loading -->
    <div class="loading-card" v-if="loading">
      <i class="fa-solid fa-spinner fa-spin"></i>
      <span>{{ loadingMsg }}</span>
    </div>

    <!-- Product list -->
    <div v-if="!loading && productosFiltrados.length > 0">
      <div class="prod-card" v-for="producto in productosVisibles" :key="producto.sku + producto.asin">
        <div class="prod-card__img" @click="verImagenes(producto)">
          <img v-if="producto.imagen" :src="producto.imagen" class="prod-card__img-src" />
          <div v-else class="prod-card__img-ph"><i class="fa-solid fa-image"></i></div>
          <div class="prod-card__img-zoom"><i class="fa-solid fa-magnifying-glass-plus"></i></div>
        </div>

        <div class="prod-card__info">
          <h6 class="prod-card__title">{{ producto.titulo }}</h6>
          <div class="prod-card__meta">
            <span class="meta-tag">ASIN: <strong>{{ producto.asin }}</strong></span>
            <span class="meta-tag meta-tag--code">{{ producto.sku }}</span>
          </div>
        </div>

        <div class="prod-card__col">
          <span class="col-label">Precio</span>
          <span class="col-value" v-if="producto.precio">{{ producto.precio }}</span>
          <span class="col-value col-value--muted" v-else>-</span>
        </div>

        <div class="prod-card__col">
          <span class="col-label">Qty</span>
          <span class="col-value">{{ producto.cantidad }}</span>
        </div>

        <div class="prod-card__col">
          <span class="col-label">Canal</span>
          <span :class="['canal-badge', producto.fulfillment === 'DEFAULT' ? 'canal-badge--fbm' : 'canal-badge--fba']">
            {{ producto.fulfillment === 'DEFAULT' ? 'FBM' : (producto.fulfillment === 'AMAZON_NA' || producto.fulfillment === 'AMAZON_EU') ? 'FBA' : producto.fulfillment || '-' }}
          </span>
        </div>

        <div class="prod-card__col">
          <span class="col-label">Estado</span>
          <span :class="['estado-badge', 'estado-badge--' + getEstadoClass(producto.estado)]">{{ producto.estado || 'N/A' }}</span>
        </div>

        <div class="prod-card__actions">
          <a class="action-btn action-btn--amazon" :href="getAmazonUrl(producto)" target="_blank" title="Ver en Amazon">
            <i class="fa-brands fa-amazon"></i>
          </a>
          <button class="action-btn action-btn--view" @click="verImagenes(producto)" title="Imagenes">
            <i class="fa-solid fa-images"></i>
          </button>
          <button class="action-btn action-btn--info" @click="verDetalle(producto)" title="Detalle">
            <i class="fa-solid fa-eye"></i>
          </button>
          <button class="action-btn action-btn--edit" @click="abrirEditar(producto)" title="Editar">
            <i class="fa-solid fa-pen"></i>
          </button>
        </div>
      </div>

      <div ref="scrollTrigger"></div>
      <div class="scroll-info">Mostrando {{ productosVisibles.length }} de {{ productosFiltrados.length }}</div>
    </div>

    <!-- Empty -->
    <div class="empty-card" v-if="!loading && productos.length > 0 && productosFiltrados.length === 0">
      <i class="fa-solid fa-filter-circle-xmark"></i>
      <span>No hay productos que coincidan con el filtro</span>
    </div>

    <!-- Modal Imagenes -->
    <b-modal ref="modal-imagenes" modal-class="modal-ap" size="xl" hide-header hide-footer centered>
      <div class="modal-inner">
        <div class="modal-hdr">
          <div class="modal-hdr__icon modal-hdr__icon--primary"><i class="fa-solid fa-images"></i></div>
          <h4 class="modal-hdr__title">Imagenes</h4>
          <p class="modal-hdr__sub">{{ imagenesProducto.titulo }}</p>
        </div>

        <div class="modal-body-custom">
          <div v-if="imagenesLoading" class="modal-loading">
            <i class="fa-solid fa-spinner fa-spin"></i>
            <span>Cargando imagenes desde Amazon...</span>
          </div>
          <div v-else-if="imagenes.length > 0">
            <div class="img-grid">
              <div v-for="(img, i) in imagenes" :key="i" class="img-thumb" :class="{ 'img-thumb--active': imagenSeleccionada === i }" @click="imagenSeleccionada = i">
                <img :src="img.link" :alt="img.variant || 'imagen'" class="img-thumb__src" />
                <div class="img-thumb__info">
                  <span class="img-thumb__variant">{{ img.variant || 'MAIN' }}</span>
                  <span class="img-thumb__size" v-if="img.width">{{ img.width }}x{{ img.height }}</span>
                </div>
                <button class="img-thumb__delete" :disabled="imagenesDeleting === i" @click.stop="borrarImagen(img, i)">
                  <b-spinner v-if="imagenesDeleting === i" small />
                  <i v-else class="fa-solid fa-trash"></i>
                </button>
              </div>
            </div>
            <div v-if="imagenSeleccionada !== null" class="img-preview">
              <img :src="imagenes[imagenSeleccionada].link" alt="preview" class="img-preview__src" />
              <p class="img-preview__info">{{ imagenes[imagenSeleccionada].variant }} <span v-if="imagenes[imagenSeleccionada].width">- {{ imagenes[imagenSeleccionada].width }}x{{ imagenes[imagenSeleccionada].height }}</span></p>
            </div>
          </div>
          <div v-else class="modal-empty"><i class="fa-solid fa-image"></i><span>No se encontraron imagenes</span></div>
        </div>

        <div class="modal-ftr">
          <button class="btn-modal btn-modal--ghost" @click="$refs['modal-imagenes'].hide()">Cerrar</button>
        </div>
      </div>
    </b-modal>

    <!-- Modal Detalle -->
    <b-modal ref="modal-detalle" modal-class="modal-ap" size="lg" hide-header hide-footer centered>
      <div class="modal-inner">
        <div class="modal-hdr">
          <div class="modal-hdr__icon modal-hdr__icon--info"><i class="fa-solid fa-eye"></i></div>
          <h4 class="modal-hdr__title">Detalle del producto</h4>
          <p class="modal-hdr__sub">{{ detalleProducto.titulo }}</p>
        </div>

        <div class="modal-body-custom" v-if="detalleProducto.asin">
          <div class="detalle-layout">
            <div class="detalle-left">
              <img v-if="detalleProducto.imagen" :src="detalleProducto.imagen" alt="img" class="detalle-img" />
              <div class="detalle-meta">
                <div class="detalle-row"><span class="detalle-label">ASIN</span><span class="detalle-val detalle-val--mono">{{ detalleProducto.asin }}</span></div>
                <div class="detalle-row"><span class="detalle-label">SKU</span><span class="detalle-val detalle-val--mono detalle-val--accent">{{ detalleProducto.sku }}</span></div>
                <div class="detalle-row"><span class="detalle-label">Estado</span><span :class="['estado-badge', 'estado-badge--' + getEstadoClass(detalleProducto.estado)]">{{ detalleProducto.estado }}</span></div>
                <div class="detalle-row"><span class="detalle-label">Precio</span><span class="detalle-val detalle-val--mono">{{ detalleProducto.precio || '-' }}</span></div>
                <div class="detalle-row"><span class="detalle-label">Cantidad</span><span class="detalle-val detalle-val--mono">{{ detalleProducto.cantidad }}</span></div>
                <div class="detalle-row"><span class="detalle-label">Canal</span><span class="detalle-val">{{ detalleProducto.fulfillment }}</span></div>
              </div>
            </div>
            <div class="detalle-right">
              <div v-if="detalleProducto.descripcion" class="detalle-section">
                <span class="detalle-section__title">Descripcion</span>
                <p class="detalle-section__text">{{ detalleProducto.descripcion }}</p>
              </div>
              <div v-if="detalleLoading" class="modal-loading modal-loading--sm"><i class="fa-solid fa-spinner fa-spin"></i><span>Cargando listing...</span></div>
              <div v-if="detalleCompleto">
                <div v-if="detalleCompleto.issues && detalleCompleto.issues.length > 0" class="detalle-section">
                  <span class="detalle-section__title">Issues</span>
                  <div class="issue-item" v-for="(issue, i) in detalleCompleto.issues" :key="i">
                    <i class="fa-solid fa-triangle-exclamation"></i>
                    <div><strong>{{ issue.code }}</strong><br/>{{ issue.message }}</div>
                  </div>
                </div>
                <div v-if="detalleCompleto.attributes" class="detalle-section">
                  <span class="detalle-section__title">Atributos</span>
                  <div class="attrs-scroll">
                    <div v-for="(val, key) in detalleCompleto.attributes" :key="key" class="attr-item">
                      <span class="attr-item__key">{{ key }}</span>
                      <pre class="attr-item__val">{{ JSON.stringify(val, null, 2) }}</pre>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-ftr">
          <button class="btn-modal btn-modal--ghost" @click="$refs['modal-detalle'].hide()">Cerrar</button>
          <button class="btn-modal btn-modal--primary" @click="$refs['modal-detalle'].hide(); abrirEditar(detalleProducto)">
            <i class="fa-solid fa-pen"></i> Editar
          </button>
        </div>
      </div>
    </b-modal>

    <!-- Modal Editar -->
    <b-modal ref="modal-editar" modal-class="modal-ap" size="lg" hide-header hide-footer centered>
      <div class="modal-inner">
        <div class="modal-hdr">
          <div class="modal-hdr__icon modal-hdr__icon--warning"><i class="fa-solid fa-pen"></i></div>
          <h4 class="modal-hdr__title">Editar producto</h4>
          <p class="modal-hdr__sub">{{ editForm.sku }}</p>
        </div>

        <div class="modal-body-custom">
          <div v-if="editLoading" class="modal-loading"><i class="fa-solid fa-spinner fa-spin"></i><span>Cargando datos del listing...</span></div>
          <div v-else>
            <div class="field" style="margin-bottom: 14px;">
              <label class="field__label">Titulo</label>
              <b-form-input v-model="editForm.titulo" class="field__input" />
            </div>
            <div class="field" style="margin-bottom: 14px;">
              <label class="field__label">Descripcion</label>
              <b-form-textarea v-model="editForm.descripcion" rows="3" class="field__input" />
            </div>
            <div class="field" style="margin-bottom: 14px;">
              <label class="field__label">Bullet Points <span style="text-transform: none; font-weight: 400; color: #94A3B8;">(uno por linea)</span></label>
              <b-form-textarea v-model="editForm.bulletPoints" rows="4" class="field__input" placeholder="Punto 1&#10;Punto 2&#10;Punto 3" />
            </div>
            <div class="field" style="margin-bottom: 14px;">
              <label class="field__label">URL imagen principal</label>
              <b-form-input v-model="editForm.imagenUrl" class="field__input" placeholder="https://..." />
            </div>
            <div class="edit-grid">
              <div class="field">
                <label class="field__label">SKU</label>
                <b-form-input v-model="editForm.sku" disabled class="field__input field__input--disabled" />
              </div>
              <div class="field">
                <label class="field__label">Product Type</label>
                <b-form-input v-model="editForm.productType" disabled class="field__input field__input--disabled" />
              </div>
            </div>
          </div>
        </div>

        <div class="modal-ftr">
          <button class="btn-modal btn-modal--ghost" @click="$refs['modal-editar'].hide()">Cancelar</button>
          <button class="btn-modal btn-modal--primary" @click="guardarCambios(); $refs['modal-editar'].hide()" :disabled="editLoading || !editForm.productType">
            <i class="fa-solid fa-check"></i> Guardar cambios
          </button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import axios from '@axios'
import vSelect from 'vue-select'

export default {
  components: { vSelect },
  data() {
    return {
      marketplace: 'UK',
      marketplaceOptions: [
        { label: 'UK', value: 'UK' }, { label: 'US', value: 'US' }, { label: 'ES', value: 'ES' },
        { label: 'DE', value: 'DE' }, { label: 'FR', value: 'FR' }, { label: 'IT', value: 'IT' },
      ],
      filtro: '', filtroEstado: null, estadoOptions: ['Active', 'Inactive', 'Incomplete'],
      loading: false, loadingMsg: 'Cargando productos...', productos: [],
      countItemView: 50, nextScrollItemView: 50, defaultItemView: 50,
      imagenesProducto: {}, imagenes: [], imagenesLoading: false, imagenSeleccionada: null, imagenesDeleting: null, imagenesListingData: null, thumbnailCache: {},
      detalleProducto: {}, detalleCompleto: null, detalleLoading: false,
      editForm: { sku: '', titulo: '', descripcion: '', bulletPoints: '', imagenUrl: '', productType: '' }, editLoading: false,
    }
  },
  computed: {
    productosFiltrados() {
      let lista = this.productos
      if (this.filtroEstado) lista = lista.filter(p => p.estado && p.estado.toLowerCase() === this.filtroEstado.toLowerCase())
      if (this.filtro) { const term = this.filtro.toLowerCase(); lista = lista.filter(p => (p.titulo && p.titulo.toLowerCase().includes(term)) || (p.sku && p.sku.toLowerCase().includes(term)) || (p.asin && p.asin.toLowerCase().includes(term))) }
      return lista
    },
    productosVisibles() { return this.productosFiltrados.slice(0, this.countItemView) },
  },
  watch: {
    filtro() { this.countItemView = this.defaultItemView },
    filtroEstado() { this.countItemView = this.defaultItemView },
    productosVisibles() { this.cargarThumbnails() },
  },
  methods: {
    handleScroll() {
      const bottomOfWindow = (window.innerHeight + window.scrollY) >= (document.documentElement.scrollHeight - 200)
      if (bottomOfWindow && this.countItemView < this.productosFiltrados.length && !this.loading) this.countItemView += this.nextScrollItemView
    },
    async cargarThumbnails() {
      const pendientes = this.productosVisibles.filter(p => !p.imagen && p.asin && !this.thumbnailCache[p.asin])
      for (const producto of pendientes) {
        if (this.thumbnailCache[producto.asin]) { this.$set(producto, 'imagen', this.thumbnailCache[producto.asin]); continue }
        try {
          const res = await axios.get('/amazon/images/' + producto.asin, { params: { marketplace: this.marketplace } })
          const data = res.data
          if (data.thumbnail) { this.$set(producto, 'imagen', data.thumbnail); this.thumbnailCache[producto.asin] = data.thumbnail }
          else if (data.images && data.images.length > 0) { const thumb = data.images[0].replace(/_AC_SL\d+_/, '_AC_SL200_'); this.$set(producto, 'imagen', thumb); this.thumbnailCache[producto.asin] = thumb }
        } catch (e) {}
      }
    },
    cargarProductos() {
      this.loading = true; this.loadingMsg = 'Generando reporte... puede tardar hasta 1 minuto'
      axios.get('/amazon/products', { params: { marketplace: this.marketplace } }).then(r => r.data).then(res => {
        this.productos = this.parsearReporte(res); this.loading = false; this.countItemView = this.defaultItemView
      }).catch(e => { this.loading = false; this.$bvToast.toast('Error cargando productos', { title: 'Amazon', variant: 'danger', solid: true }) })
    },
    parsearReporte(data) {
      if (!Array.isArray(data)) return []
      return data.map(item => ({ sku: item['seller-sku'] || '', asin: item['asin1'] || '', titulo: item['item-name'] || '', descripcion: item['item-description'] || '', precio: item['price'] || '', cantidad: item['quantity'] || '', estado: item['status'] || '', imagen: '', fulfillment: item['fulfillment-channel'] || '', productId: item['product-id'] || '', raw: item }))
    },
    verImagenes(producto) {
      this.imagenesProducto = producto; this.imagenes = []; this.imagenSeleccionada = null; this.imagenesDeleting = null; this.imagenesListingData = null; this.imagenesLoading = true; this.$refs['modal-imagenes'].show()
      if (producto.asin) {
        const imgP = axios.get('/amazon/images/' + producto.asin, { params: { marketplace: this.marketplace } })
        const listP = producto.sku ? axios.get('/amazon/products/listing/' + encodeURIComponent(producto.sku), { params: { marketplace: this.marketplace } }).catch(() => null) : Promise.resolve(null)
        Promise.all([imgP, listP]).then(([imgRes, listingRes]) => {
          const res = imgRes.data
          if (res.details && res.details.length > 0) this.imagenes = res.details
          else this.imagenes = (res.images || []).map((url, i) => ({ link: url, variant: i === 0 ? 'MAIN' : 'PT0' + i }))
          if (this.imagenes.length > 0) { this.imagenSeleccionada = 0; this.$set(producto, 'imagen', this.imagenes[0].link); this.thumbnailCache[producto.asin] = this.imagenes[0].link }
          if (listingRes && listingRes.data) this.imagenesListingData = listingRes.data
          this.imagenesLoading = false
        }).catch(() => { this.imagenesLoading = false })
      } else this.imagenesLoading = false
    },
    verDetalle(item) {
      this.detalleProducto = item; this.detalleCompleto = null; this.$refs['modal-detalle'].show()
      if (item.sku) { this.detalleLoading = true; axios.get('/amazon/products/listing/' + encodeURIComponent(item.sku), { params: { marketplace: this.marketplace } }).then(r => r.data).then(res => { this.detalleCompleto = res; this.detalleLoading = false }).catch(() => { this.detalleLoading = false }) }
    },
    abrirEditar(item) {
      this.editForm = { sku: item.sku, titulo: item.titulo, descripcion: '', bulletPoints: '', imagenUrl: item.imagen || '', productType: '' }
      this.editLoading = true; this.$refs['modal-editar'].show()
      if (item.sku) {
        axios.get('/amazon/products/listing/' + encodeURIComponent(item.sku), { params: { marketplace: this.marketplace } }).then(r => r.data).then(res => {
          if (res.attributes) {
            if (res.attributes.product_description) { const d = res.attributes.product_description; this.editForm.descripcion = Array.isArray(d) ? (d[0] && d[0].value || '') : (d.value || '') }
            if (res.attributes.bullet_point) { const b = res.attributes.bullet_point; this.editForm.bulletPoints = Array.isArray(b) ? b.map(x => x.value || x).join('\n') : (b.value || '') }
            if (res.attributes.item_name) { const n = res.attributes.item_name; this.editForm.titulo = Array.isArray(n) ? (n[0] && n[0].value || '') : (n.value || '') }
          }
          if (res.summaries && res.summaries[0]) this.editForm.productType = res.summaries[0].productType || ''
          this.editLoading = false
        }).catch(() => { this.editLoading = false; this.$bvToast.toast('Error cargando listing', { title: 'Amazon', variant: 'danger', solid: true }) })
      } else this.editLoading = false
    },
    guardarCambios() {
      if (!this.editForm.sku || !this.editForm.productType) return
      const mktIds = { US: 'ATVPDKIKX0DER', UK: 'A1F83G8C2ARO7P', ES: 'A1RKKUPIHCS9HS', DE: 'A1PA6795UKMFR9', FR: 'A13V1IB3VIYZZH', IT: 'APJ6JRA9NG5V4' }
      const mktId = mktIds[this.marketplace] || mktIds['UK']; const attributes = {}
      if (this.editForm.titulo) attributes.item_name = [{ value: this.editForm.titulo, marketplace_id: mktId }]
      if (this.editForm.descripcion) attributes.product_description = [{ value: this.editForm.descripcion, marketplace_id: mktId }]
      if (this.editForm.bulletPoints) { const bullets = this.editForm.bulletPoints.split('\n').filter(b => b.trim()); attributes.bullet_point = bullets.map(b => ({ value: b.trim(), marketplace_id: mktId })) }
      if (this.editForm.imagenUrl) attributes.main_product_image_locator = [{ value: this.editForm.imagenUrl, marketplace_id: mktId }]
      axios.patch('/amazon/products/listing/' + encodeURIComponent(this.editForm.sku), { marketplace: this.marketplace, productType: this.editForm.productType, attributes }).then(() => {
        this.$bvToast.toast('Producto actualizado', { title: 'Amazon', variant: 'success', solid: true })
        const idx = this.productos.findIndex(p => p.sku === this.editForm.sku)
        if (idx > -1 && this.editForm.titulo) this.productos[idx].titulo = this.editForm.titulo
      }).catch(e => { this.$bvToast.toast('Error actualizando', { title: 'Amazon', variant: 'danger', solid: true }) })
    },
    borrarImagen(img, index) {
      const producto = this.imagenesProducto; if (!producto.sku) return
      const variantMap = { MAIN: 'main_product_image_locator', PT01: 'other_product_image_locator_1', PT02: 'other_product_image_locator_2', PT03: 'other_product_image_locator_3', PT04: 'other_product_image_locator_4', PT05: 'other_product_image_locator_5', PT06: 'other_product_image_locator_6', PT07: 'other_product_image_locator_7', PT08: 'other_product_image_locator_8' }
      const attrKey = variantMap[img.variant]; if (!attrKey) return
      const productType = (this.imagenesListingData && this.imagenesListingData.summaries && this.imagenesListingData.summaries[0] && this.imagenesListingData.summaries[0].productType) || ''; if (!productType) return
      if (!confirm('Borrar imagen ' + img.variant + '?')) return
      this.imagenesDeleting = index; const attributes = {}; attributes[attrKey] = []
      axios.patch('/amazon/products/listing/' + encodeURIComponent(producto.sku), { marketplace: this.marketplace, productType, attributes }).then(() => {
        this.$bvToast.toast('Imagen eliminada', { title: 'Amazon', variant: 'success', solid: true }); this.imagenes.splice(index, 1)
        if (this.imagenSeleccionada >= this.imagenes.length) this.imagenSeleccionada = this.imagenes.length > 0 ? 0 : null; this.imagenesDeleting = null
      }).catch(() => { this.$bvToast.toast('Error borrando imagen', { title: 'Amazon', variant: 'danger', solid: true }); this.imagenesDeleting = null })
    },
    getAmazonUrl(p) { const d = { UK: 'amazon.co.uk', US: 'amazon.com', ES: 'amazon.es', DE: 'amazon.de', FR: 'amazon.fr', IT: 'amazon.it' }; return 'https://www.' + (d[this.marketplace] || 'amazon.co.uk') + '/dp/' + p.asin },
    getEstadoVariant(e) { if (!e) return 'secondary'; const s = e.toLowerCase(); if (s === 'active') return 'success'; if (s === 'inactive' || s === 'incomplete') return 'warning'; if (s === 'deleted' || s === 'suppressed') return 'danger'; return 'info' },
    getEstadoClass(e) { if (!e) return 'neutral'; const s = e.toLowerCase(); if (s === 'active') return 'active'; if (s === 'inactive' || s === 'incomplete') return 'warning'; return 'danger' },
  },
  mounted() { this.cargarProductos(); window.addEventListener('scroll', this.handleScroll) },
  beforeDestroy() { window.removeEventListener('scroll', this.handleScroll) },
}
</script>

<style lang="scss" scoped>
$primary: #3B82F6;
$success: #10B981;
$danger: #EF4444;
$warning: #F59E0B;
$info: #06B6D4;
$border: #E2E8F0;
$text: #1E293B;
$text-muted: #64748B;
$bg: #F8FAFC;
$card-bg: #FFFFFF;
$radius: 12px;
$radius-sm: 8px;
$shadow: 0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04);

.amzprod-page { max-width: 1300px; margin: 0 auto; padding: 0 8px; }

.ap-header { display: flex; justify-content: space-between; align-items: flex-end; margin-bottom: 20px; }
.header-eyebrow { display: block; font-family: 'JetBrains Mono', monospace; font-size: 11px; font-weight: 600; letter-spacing: 1.5px; text-transform: uppercase; color: #FF9900; margin-bottom: 2px; }
.header-title { font-family: 'Plus Jakarta Sans', sans-serif; font-size: 24px; font-weight: 700; color: $text; margin: 0; }

.btn-ap {
  display: inline-flex; align-items: center; gap: 8px; padding: 9px 20px; border-radius: $radius-sm; border: none;
  font-family: 'Plus Jakarta Sans', sans-serif; font-size: 13px; font-weight: 600; cursor: pointer; transition: all 0.2s ease;
  &--primary { background: $primary; color: white; &:hover { background: darken($primary, 8%); } }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
}

// Filters
.filters-bar {
  display: flex; align-items: flex-end; gap: 16px; padding: 16px 20px; background: $card-bg; border: 1px solid $border;
  border-radius: $radius; box-shadow: $shadow; margin-bottom: 20px; flex-wrap: wrap;
}

.filter-group { display: flex; flex-direction: column; gap: 5px; }
.filter-label { font-family: 'JetBrains Mono', monospace; font-size: 10px; font-weight: 600; letter-spacing: 0.5px; text-transform: uppercase; color: $text-muted; margin: 0; }

.mkt-pills, .estado-pills { display: flex; gap: 3px; }
.mkt-pill {
  padding: 5px 10px; border-radius: 6px; border: 1px solid $border; background: transparent;
  font-family: 'JetBrains Mono', monospace; font-size: 11px; font-weight: 600; color: $text-muted; cursor: pointer; transition: all 0.15s;
  &:hover { border-color: #FF9900; color: #FF9900; }
  &.active { background: #FF9900; border-color: #FF9900; color: white; }
}
.pill {
  padding: 5px 11px; border-radius: 20px; border: 1px solid $border; background: transparent;
  font-family: 'JetBrains Mono', monospace; font-size: 10px; font-weight: 500; color: $text-muted; cursor: pointer; transition: all 0.15s;
  &:hover { border-color: $primary; color: $primary; }
  &.active { background: $primary; border-color: $primary; color: white; }
}

.search-box {
  position: relative; flex: 1; min-width: 200px; max-width: 320px;
  &__icon { position: absolute; left: 10px; top: 50%; transform: translateY(-50%); color: $text-muted; font-size: 12px; }
  &__input { width: 100%; padding: 7px 12px 7px 32px; border: 1px solid $border; border-radius: $radius-sm; font-family: 'Plus Jakarta Sans', sans-serif; font-size: 13px; color: $text; background: transparent; &::placeholder { color: #CBD5E1; } &:focus { outline: none; border-color: $primary; } }
}

.filter-count { font-family: 'JetBrains Mono', monospace; font-size: 12px; color: $text-muted; margin-left: auto; }

// Loading / Empty
.loading-card, .empty-card {
  display: flex; flex-direction: column; align-items: center; gap: 12px; padding: 60px 0;
  background: $card-bg; border: 1px solid $border; border-radius: $radius; box-shadow: $shadow;
  i { font-size: 28px; color: $primary; }
  span { font-family: 'JetBrains Mono', monospace; font-size: 13px; color: $text-muted; }
}
.empty-card i { color: #CBD5E1; }

// Product card
.prod-card {
  display: flex; align-items: center; gap: 16px; padding: 12px 18px; background: $card-bg;
  border: 1px solid $border; border-radius: $radius-sm; margin-bottom: 8px; transition: box-shadow 0.2s; box-shadow: $shadow;
  &:hover { box-shadow: 0 4px 12px rgba(0,0,0,0.06); }

  &__img { width: 60px; height: 60px; border-radius: 8px; overflow: hidden; background: $bg; cursor: pointer; position: relative; flex-shrink: 0; }
  &__img-src { width: 100%; height: 100%; object-fit: contain; padding: 4px; }
  &__img-ph { width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; color: #CBD5E1; font-size: 20px; }
  &__img-zoom { position: absolute; inset: 0; background: rgba(0,0,0,0.35); display: flex; align-items: center; justify-content: center; color: white; font-size: 14px; opacity: 0; transition: opacity 0.2s; }
  &__img:hover &__img-zoom { opacity: 1; }

  &__info { flex: 1; min-width: 0; }
  &__title { font-family: 'Plus Jakarta Sans', sans-serif; font-size: 13px; font-weight: 600; color: $text; margin: 0 0 4px; line-height: 1.3; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
  &__meta { display: flex; gap: 8px; flex-wrap: wrap; }

  &__col { display: flex; flex-direction: column; align-items: center; min-width: 60px; }
  &__actions { display: flex; gap: 4px; flex-shrink: 0; }
}

.meta-tag { font-family: 'JetBrains Mono', monospace; font-size: 10px; color: $text-muted; &--code { background: $bg; padding: 1px 6px; border-radius: 4px; color: $primary; font-weight: 500; } }
.col-label { font-family: 'JetBrains Mono', monospace; font-size: 9px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; color: $text-muted; }
.col-value { font-family: 'JetBrains Mono', monospace; font-size: 13px; font-weight: 600; color: $text; font-feature-settings: "tnum"; margin-top: 2px; &--muted { color: #CBD5E1; } }

.canal-badge { padding: 2px 8px; border-radius: 4px; font-family: 'JetBrains Mono', monospace; font-size: 10px; font-weight: 700; margin-top: 2px; &--fbm { background: rgba($info, 0.1); color: $info; } &--fba { background: rgba($warning, 0.1); color: darken($warning, 10%); } }
.estado-badge { padding: 2px 8px; border-radius: 4px; font-family: 'JetBrains Mono', monospace; font-size: 10px; font-weight: 700; margin-top: 2px; &--active { background: rgba($success, 0.1); color: $success; } &--warning { background: rgba($warning, 0.1); color: darken($warning, 10%); } &--danger { background: rgba($danger, 0.1); color: $danger; } &--neutral { background: rgba($text-muted, 0.08); color: $text-muted; } }

.action-btn {
  width: 30px; height: 30px; border-radius: 6px; border: 1px solid transparent; background: transparent; color: $text-muted; cursor: pointer;
  display: flex; align-items: center; justify-content: center; transition: all 0.15s; font-size: 12px; text-decoration: none;
  &--amazon:hover { background: rgba(#FF9900, 0.1); color: #FF9900; border-color: rgba(#FF9900, 0.2); }
  &--view:hover { background: rgba($primary, 0.1); color: $primary; border-color: rgba($primary, 0.2); }
  &--info:hover { background: rgba($info, 0.1); color: $info; border-color: rgba($info, 0.2); }
  &--edit:hover { background: rgba($warning, 0.1); color: $warning; border-color: rgba($warning, 0.2); }
}

.scroll-info { text-align: center; padding: 12px; font-family: 'JetBrains Mono', monospace; font-size: 12px; color: $text-muted; }

// Responsive
@media (max-width: 992px) {
  .prod-card { flex-wrap: wrap; }
  .prod-card__col { min-width: 50px; }
}
@media (max-width: 768px) {
  .filters-bar { flex-direction: column; align-items: stretch; }
  .search-box { max-width: none; }
  .ap-header { flex-direction: column; align-items: flex-start; gap: 12px; }
}
</style>

<style lang="scss">
@import '../../@core/scss/vue/libs/vue-select.scss';

// Modal base
.modal-ap {
  .modal-content { border-radius: 20px; border: 1px solid #E2E8F0; box-shadow: 0 25px 60px rgba(0,0,0,0.15), 0 10px 24px rgba(0,0,0,0.08); overflow: hidden; }
  .modal-body { padding: 0; }
}

.modal-inner { padding: 0; }

.modal-hdr {
  text-align: center; padding: 28px 28px 0;
  &__icon { width: 48px; height: 48px; border-radius: 12px; display: inline-flex; align-items: center; justify-content: center; margin-bottom: 14px; font-size: 18px;
    &--primary { background: rgba(#3B82F6, 0.1); color: #3B82F6; }
    &--info { background: rgba(#06B6D4, 0.1); color: #06B6D4; }
    &--warning { background: rgba(#F59E0B, 0.1); color: #F59E0B; }
  }
  &__title { font-family: 'Plus Jakarta Sans', sans-serif; font-size: 18px; font-weight: 700; color: #1E293B; margin: 0 0 4px; }
  &__sub { font-family: 'JetBrains Mono', monospace; font-size: 12px; color: #64748B; margin: 0; max-width: 400px; margin-left: auto; margin-right: auto; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
}

.modal-body-custom { padding: 22px 28px; }
.modal-ftr { display: flex; justify-content: flex-end; gap: 8px; padding: 0 28px 24px; }

.modal-loading { display: flex; flex-direction: column; align-items: center; gap: 10px; padding: 40px 0; color: #3B82F6; i { font-size: 24px; } span { font-family: 'JetBrains Mono', monospace; font-size: 12px; color: #64748B; } &--sm { padding: 20px 0; } }
.modal-empty { display: flex; flex-direction: column; align-items: center; gap: 8px; padding: 40px 0; i { font-size: 28px; color: #CBD5E1; } span { font-family: 'JetBrains Mono', monospace; font-size: 12px; color: #94A3B8; } }

.btn-modal {
  display: inline-flex; align-items: center; gap: 6px; padding: 9px 18px; border-radius: 8px; border: none;
  font-family: 'Plus Jakarta Sans', sans-serif; font-size: 13px; font-weight: 600; cursor: pointer; transition: all 0.15s;
  &--primary { background: #3B82F6; color: white; &:hover { background: #2563EB; } &:disabled { opacity: 0.5; cursor: not-allowed; } }
  &--ghost { background: transparent; color: #64748B; &:hover { background: #F1F5F9; color: #1E293B; } }
}

// Field
.field {
  display: flex; flex-direction: column; gap: 5px;
  &__label { font-family: 'JetBrains Mono', monospace; font-size: 10px; font-weight: 600; letter-spacing: 0.5px; text-transform: uppercase; color: #64748B; margin: 0; }
  &__input { border-radius: 8px !important; border: 1px solid #E2E8F0 !important; font-size: 13px !important; &:focus { border-color: #3B82F6 !important; box-shadow: 0 0 0 3px rgba(#3B82F6, 0.1) !important; } &--disabled { background: #F8FAFC !important; color: #94A3B8 !important; } }
}

.edit-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; margin-top: 14px; }

// Detalle layout
.detalle-layout { display: grid; grid-template-columns: 240px 1fr; gap: 24px; }
.detalle-left {}
.detalle-img { width: 100%; object-fit: contain; border-radius: 10px; background: #F8FAFC; padding: 12px; border: 1px solid #E2E8F0; }
.detalle-meta { margin-top: 14px; background: #F8FAFC; border: 1px solid #E2E8F0; border-radius: 10px; padding: 4px 0; }
.detalle-row { display: flex; justify-content: space-between; align-items: center; padding: 7px 14px; & + & { border-top: 1px solid #F1F5F9; } }
.detalle-label { font-family: 'JetBrains Mono', monospace; font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; color: #94A3B8; }
.detalle-val { font-family: 'Plus Jakarta Sans', sans-serif; font-size: 13px; font-weight: 500; color: #1E293B; &--mono { font-family: 'JetBrains Mono', monospace; font-size: 12px; font-feature-settings: "tnum"; } &--accent { color: #3B82F6; } }

.detalle-right {}
.detalle-section { margin-bottom: 16px; &__title { display: block; font-family: 'JetBrains Mono', monospace; font-size: 10px; font-weight: 600; letter-spacing: 1px; text-transform: uppercase; color: #64748B; margin-bottom: 8px; } &__text { font-family: 'Plus Jakarta Sans', sans-serif; font-size: 13px; color: #1E293B; line-height: 1.6; margin: 0; } }

.issue-item { display: flex; gap: 10px; padding: 10px 12px; background: rgba(#F59E0B, 0.06); border: 1px solid rgba(#F59E0B, 0.15); border-radius: 8px; margin-bottom: 6px; font-size: 12px; color: #92400E; i { margin-top: 2px; } }

.attrs-scroll { max-height: 350px; overflow-y: auto; }
.attr-item { margin-bottom: 8px; &__key { display: block; font-family: 'JetBrains Mono', monospace; font-size: 11px; font-weight: 600; color: #64748B; } &__val { font-family: 'JetBrains Mono', monospace; font-size: 11px; max-height: 70px; overflow: auto; background: #F8FAFC; padding: 6px 10px; border-radius: 6px; border: 1px solid #E2E8F0; margin-top: 3px; color: #1E293B; } }

// Image grid & thumbs
.img-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; }

.img-thumb {
  cursor: pointer; padding: 8px; border-radius: 8px; border: 2px solid transparent; transition: all 0.2s; position: relative;
  &:hover { border-color: #3B82F6; background: rgba(#3B82F6, 0.03); }
  &--active { border-color: #3B82F6; background: rgba(#3B82F6, 0.03); }
  &__src { width: 100%; height: 180px; object-fit: contain; background: #f8f8f8; border-radius: 6px; }
  &__info { text-align: center; margin-top: 6px; }
  &__variant { font-family: 'JetBrains Mono', monospace; font-size: 10px; font-weight: 600; color: #64748B; background: #F1F5F9; padding: 2px 8px; border-radius: 4px; }
  &__size { display: block; font-family: 'JetBrains Mono', monospace; font-size: 10px; color: #94A3B8; margin-top: 2px; }
  &__delete { position: absolute; top: 12px; right: 12px; width: 26px; height: 26px; border-radius: 6px; border: none; background: rgba(#EF4444, 0.1); color: #EF4444; cursor: pointer; display: flex; align-items: center; justify-content: center; font-size: 11px; opacity: 0; transition: opacity 0.15s; }
  &:hover &__delete { opacity: 1; }
}

.img-preview { text-align: center; margin-top: 16px; padding-top: 16px; border-top: 1px solid #E2E8F0;
  &__src { max-height: 500px; max-width: 100%; object-fit: contain; }
  &__info { margin-top: 8px; font-family: 'JetBrains Mono', monospace; font-size: 12px; color: #64748B; }
}

// Dark mode
.dark-layout {
  .amzprod-page {
    .header-title { color: #E2E8F0; }
    .filters-bar { background: #1E293B; border-color: #334155; }
    .search-box__input { border-color: #334155; color: #E2E8F0; &::placeholder { color: #475569; } }
    .mkt-pill { border-color: #475569; color: #94A3B8; &:hover { border-color: #FF9900; color: #FF9900; } &.active { background: #FF9900; border-color: #FF9900; color: white; } }
    .pill { border-color: #475569; color: #94A3B8; &.active { background: #3B82F6; border-color: #3B82F6; color: white; } }
    .loading-card, .empty-card { background: #1E293B; border-color: #334155; }
    .prod-card { background: #1E293B; border-color: #334155; &:hover { box-shadow: 0 4px 12px rgba(0,0,0,0.2); } }
    .prod-card__img { background: #334155; }
    .prod-card__title { color: #E2E8F0; }
    .col-value { color: #F1F5F9; }
    .action-btn { color: #94A3B8; }
  }

  .modal-ap .modal-content { background: #1E293B; border-color: #334155; }
  .modal-hdr__title { color: #E2E8F0; }
  .modal-hdr__sub { color: #64748B; }
  .field__label { color: #94A3B8; }
  .field__input { background: #0F172A !important; border-color: #334155 !important; color: #E2E8F0 !important; &:focus { border-color: #3B82F6 !important; } }
  .field__input--disabled { background: #1E293B !important; color: #64748B !important; }
  .btn-modal--ghost { color: #94A3B8; &:hover { background: rgba(255,255,255,0.05); color: #E2E8F0; } }
  .detalle-img { background: #334155; border-color: #334155; }
  .detalle-meta { background: #0F172A; border-color: #334155; }
  .detalle-row + .detalle-row { border-color: #1E293B; }
  .detalle-val { color: #E2E8F0; }
  .detalle-section__text { color: #E2E8F0; }
  .issue-item { background: rgba(#F59E0B, 0.08); border-color: rgba(#F59E0B, 0.15); color: #FCD34D; }
  .attr-item__val { background: #0F172A; border-color: #334155; color: #E2E8F0; }
  .img-thumb { &:hover, &--active { border-color: #3B82F6; background: rgba(#3B82F6, 0.05); } }
  .img-thumb__src { background: #334155; }
  .img-preview { border-color: #334155; }
}
</style>
